import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    CircularProgress,
    Alert,
    MenuItem,
    TextField,
    IconButton,
    Tooltip,
    Tabs,
    Tab,
    TablePagination
} from '@mui/material';
import {
    DollarSign,
    Building2,
    Users,
    FileText,
    BarChart3,
    RefreshCw,
    Laptop,
    UserCheck,
    Briefcase,
    Folder,
    ArrowLeft,
    PieChart as PieChartIcon
} from 'lucide-react';
import {
    BarChart,
    Bar,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import DownloadIcon from '@mui/icons-material/Download';
import PdfIcon from '@mui/icons-material/PictureAsPdf';
   import VisibilityIcon from '@mui/icons-material/Visibility';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(timezone);

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const PIE_COLORS = ['#1a237e', '#0288d1', '#ed6c02', '#2e7d32', '#7b1fa2', '#c62828'];

const getBase64ImageFromUrl = async (imgUrl) => {
    try {
        const response = await fetch(imgUrl);
        const blob = await response.blob();
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    } catch (err) {
        //console.error('Error converting logo to base64:', err);
        return null;
    }
};

const CostCenterDashboard = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [summary, setSummary] = useState(null);
    const [costCenters, setCostCenters] = useState([]);
    const [selectedCostCenterId, setSelectedCostCenterId] = useState('all');

    // Page view state: 'list' or 'detail'
    const [viewMode, setViewMode] = useState('list');
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailData, setDetailData] = useState(null);
    const [detailTab, setDetailTab] = useState(0);
    const [currencySymbol, setCurrencySymbol] = useState('₹');
    const [companyConfig, setCompanyConfig] = useState({ dateFormat: 'DD/MM/YYYY', timezone: 'Asia/Kolkata' });

    useEffect(() => {
        const fetchCurrency = async () => {
            const token = localStorage.getItem('token');
            if (token) {
                try {
                    const payload = JSON.parse(atob(token.split('.')[1]));
                    const company_id = payload.company_id;
                    const cmpRes = await axios.get(`${API_URL}/api/cmp/${company_id}`, { headers: { Authorization: `Bearer ${token}` } });
                    if (cmpRes.data.success && cmpRes.data.data) {
                        if (cmpRes.data.data.currency_symbol) {
                            setCurrencySymbol(cmpRes.data.data.currency_symbol);
                        }
                        setCompanyConfig({
                            dateFormat: cmpRes.data.data.dateFormat || 'DD/MM/YYYY',
                            timezone: cmpRes.data.data.timezone || 'Asia/Kolkata'
                        });
                    }
                } catch (e) {
                    // console.error("Failed to fetch company country", e);
                }
            }
        };
        fetchCurrency();
    }, []);

    const [companyDetails, setCompanyDetails] = useState(null);
    const [companyLogoBase64, setCompanyLogoBase64] = useState(null);
    const [vendors, setVendors] = useState([]);

    // Pagination & Search States
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        const loadCompanyAndVendors = async () => {
            try {
                const token = localStorage.getItem('token');
                const headers = token ? { Authorization: `Bearer ${token}` } : {};

                // 1. Fetch Company Details
                const cmpRes = await axios.get(`${API_URL}/api/cmp/current`, { headers });
                if (cmpRes.data?.success && cmpRes.data?.data) {
                    const company = cmpRes.data.data;
                    setCompanyDetails(company);
                    if (company.companyLogo) {
                        const logoUrl = company.companyLogo.startsWith('http') ? company.companyLogo : `${API_URL}/uploads/${company.companyLogo}`;
                        const base64 = await getBase64ImageFromUrl(logoUrl);
                        if (base64) setCompanyLogoBase64(base64);
                    }
                }

                // 2. Fetch Vendors
                const vendorRes = await axios.get(`${API_URL}/api/contract`, { headers });
                if (vendorRes.data?.success) {
                    setVendors(vendorRes.data.data || []);
                }
            } catch (err) {
                //console.error('Error preloading company details/vendors:', err);
            }
        };
        loadCompanyAndVendors();
    }, []);

    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            setError(null);
            const token = localStorage.getItem('token');
            const headers = token ? { Authorization: `Bearer ${token}` } : {};

            const [summaryRes, detailedRes] = await Promise.all([
                axios.get(`${API_URL}/api/cost-center-dashboard/summary`, { headers }),
                axios.get(`${API_URL}/api/cost-center-dashboard/detailed-list?page=${page + 1}&limit=${rowsPerPage}&search=${searchQuery}`, { headers })
            ]);

            if (summaryRes.data.success) setSummary(summaryRes.data.data);
            if (detailedRes.data.success) {
                setCostCenters(detailedRes.data.data.list || []);
                setTotalCount(detailedRes.data.data.pagination?.totalItems || 0);
            }
        } catch (err) {
            //console.error('Error loading cost center dashboard data:', err);
            setError(err.response?.data?.message || 'Failed to load financial dashboard analytics.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDashboardData();
    }, [page, rowsPerPage, searchQuery]);

    const handleOpenDetailView = async (costCenterId) => {
        try {
            setDetailLoading(true);
            setViewMode('detail');
            setDetailTab(0);
            const token = localStorage.getItem('token');
            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            const res = await axios.get(`${API_URL}/api/cost-center-dashboard/detail/${costCenterId}`, { headers });
            if (res.data.success) {
                setDetailData(res.data.data);
            }
        } catch (err) {
            //console.error('Error loading detail report:', err);
            alert('Failed to load detailed report for this Cost Center.');
        } finally {
            setDetailLoading(false);
        }
    };

    const handleCloseDetailView = () => {
        setViewMode('list');
        setDetailData(null);
    };

    const formatCurrency = (amount) => {
        return `${currencySymbol}${Number(amount || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        return dayjs(dateStr).tz(companyConfig.timezone).format(companyConfig.dateFormat);
    };

    const handleExportExcel = () => {
        const exportData = filteredCostCenters.map(cc => ({
            'Cost Center Name': cc.name,
            'Cost Center Code': cc.code,
            'Employee Salary (INR)': cc.employeeSalarySum,
            'Direct Contractor Cost (INR)': cc.directContractorSum,
            'Agency Contractor Cost (INR)': cc.agencyContractorSum,
            'Total Utilised Expenditure (INR)': cc.totalExpenditure,
            'Employees Count': cc.employeeCount || 0,
            'Contractors Count': cc.contractorCount || 0,
            'Projects Count': cc.projectsCount || 0
        }));

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Cost Center Summary");
        XLSX.writeFile(workbook, `Cost_Center_Utilization_Report_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    const formatCurrencyForPDF = (amount) => {
        return `${currencySymbol} ${Number(amount || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
    };

    const handleExportPDF = () => {
        const doc = new jsPDF('landscape');
        
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(16);
        doc.setTextColor(26, 35, 126);
        doc.text('Cost Center Utilization Summary Report', 14, 15);
        
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 100, 100);
        doc.text(`Generated on: ${formatDate(new Date())}`, 14, 20);
        
        const headers = [[
            'Cost Center Name', 
            'Code', 
            'Employee Salary', 
            'Direct Contractor', 
            'Agency Contractor', 
            'Total Utilised',
            'Employees',
            'Contractors',
            'Projects'
        ]];
        
        const rows = filteredCostCenters.map(cc => [
            cc.name,
            cc.code,
            formatCurrencyForPDF(cc.employeeSalarySum),
            formatCurrencyForPDF(cc.directContractorSum),
            formatCurrencyForPDF(cc.agencyContractorSum),
            formatCurrencyForPDF(cc.totalExpenditure),
            cc.employeeCount || 0,
            cc.contractorCount || 0,
            cc.projectsCount || 0
        ]);
        
        doc.autoTable({
            startY: 25,
            head: headers,
            body: rows,
            theme: 'grid',
            headStyles: { fillColor: [26, 35, 126], textColor: [255, 255, 255], fontStyle: 'bold' },
            styles: { fontSize: 9, cellPadding: 3 },
            columnStyles: {
                0: { cellWidth: 55 },
                1: { cellWidth: 20 },
                2: { halign: 'right' },
                3: { halign: 'right' },
                4: { halign: 'right' },
                5: { halign: 'right', fontStyle: 'bold' },
                6: { halign: 'center' },
                7: { halign: 'center' },
                8: { halign: 'center' }
            }
        });
        
        doc.save(`Cost_Center_Utilization_Report_${new Date().toISOString().split('T')[0]}.pdf`);
    };
    const formatDateOnly = (dateStr) => {
        if (!dateStr) return 'N/A';
        return dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
    };

    const mapInvoiceToPDFData = (inv) => {
        const isVendor = inv.invoiceNumber?.startsWith('VEND-');
        const subtotal = Number(inv.subtotal) || 0;
        const gstAmt = Number(inv.gstAmount) || 0;
        const tdsAmt = Number(inv.tdsAmount) || 0;

        const dynamicGstRate = subtotal > 0 ? ((gstAmt / subtotal) * 100).toFixed(0) : '0';
        const dynamicTdsRate = subtotal > 0 ? ((tdsAmt / subtotal) * 100).toFixed(0) : '0';

        if (isVendor) {
            const vendor = vendors.find(v => v.id === inv.contractEmployee?.companyId);
            return {
                invoiceNumber: inv.invoiceNumber,
                date: formatDateOnly(inv.invoiceDate),
                dueDate: formatDateOnly(inv.dueDate),
                currency: 'INR',
                billToCompany: companyDetails?.companyname || '',
                billToStreet: companyDetails?.address || '',
                billToCity: companyDetails?.hq || '',
                billToPhone: companyDetails?.contactNumber || '',
                billToEmail: companyDetails?.contactemail || '',
                billToGst: companyDetails?.GST || '',
                billFromName: vendor ? vendor.companyName : (inv.contractEmployee?.fullName || ''),
                billFromStreet: vendor ? vendor.companyAddress : '',
                billFromCity: 'Corporate Headquarters',
                billFromPhone: vendor ? vendor.companyPhone : '',
                billFromEmail: vendor ? vendor.companyEmail : '',
                billFromGst: vendor ? vendor.companyPAN : '',
                bankName: vendor ? vendor.bankName : '',
                accountName: vendor ? vendor.companyName : '',
                accountNumber: vendor ? vendor.accountNumber : '',
                ifscCode: vendor ? vendor.ifscCode : '',
                employeeName: vendor ? vendor.companyName : '',
                employeeDesignation: 'Consolidated Agency Payout',
                contractPeriod: inv.contractEmployee?.contractPeriod || '',
                hourlyRate: inv.hourlyRate,
                totalHours: inv.totalHours,
                contractTotal: inv.subtotal,
                gstRate: dynamicGstRate,
                tdsRate: dynamicTdsRate,
                otherChargesValue: '0',
            };
        }

        return {
            invoiceNumber: inv.invoiceNumber,
            date: formatDateOnly(inv.invoiceDate),
            dueDate: formatDateOnly(inv.dueDate),
            currency: 'INR',
            billToCompany: companyDetails?.companyname || '',
            billToStreet: companyDetails?.address || '',
            billToCity: companyDetails?.hq || '',
            billToPhone: companyDetails?.contactNumber || '',
            billToEmail: companyDetails?.contactemail || '',
            billToGst: companyDetails?.GST || '',
            billFromName: inv.contractEmployee?.fullName || '',
            billFromStreet: inv.contractEmployee?.address || '',
            billFromCity: 'Residential Address',
            billFromPhone: inv.contractEmployee?.phoneNumber || '',
            billFromEmail: inv.contractEmployee?.email || '',
            billFromGst: inv.contractEmployee?.panNumber || '',
            bankName: inv.contractEmployee?.bankName || '',
            accountName: inv.contractEmployee?.accountName || '',
            accountNumber: inv.contractEmployee?.accountNumber || '',
            ifscCode: inv.contractEmployee?.ifscCode || '',
            employeeName: inv.contractEmployee?.fullName || '',
            employeeDesignation: inv.contractEmployee?.designation || '',
            contractPeriod: inv.contractEmployee?.contractPeriod || '',
            hourlyRate: inv.hourlyRate,
            totalHours: inv.totalHours,
            contractTotal: inv.subtotal,
            gstRate: dynamicGstRate,
            tdsRate: dynamicTdsRate,
            otherChargesValue: '0',
        };
    };

    const handleViewInvoiceDocument = (inv) => {
        const pdfData = mapInvoiceToPDFData(inv);
        const doc = new jsPDF();
        const currencySym = 'Rs.';

        if (companyLogoBase64) {
            try {
                doc.addImage(companyLogoBase64, 'PNG', 14, 9, 24, 10);
            } catch (err) {}
        }

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(14);
        doc.text(pdfData.billToCompany, 105, 14, { align: 'center' });
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.text(`${pdfData.billToStreet}, ${pdfData.billToCity}`, 105, 19, { align: 'center' });
        
        doc.line(14, 29, 196, 29);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.setTextColor(26, 35, 126);
        doc.text('PRO FORMA INVOICE', 105, 35, { align: 'center' });
        doc.line(14, 39, 196, 39);

        doc.setTextColor(50, 50, 50);
        doc.setFontSize(9);
        doc.text('INVOICE DETAILS:', 14, 46);
        doc.setFont('helvetica', 'normal');
        doc.text(`Invoice No: ${pdfData.invoiceNumber}`, 14, 52);
        doc.text(`Invoice Date: ${pdfData.date}`, 14, 57);
        doc.text(`Due Date: ${pdfData.dueDate}`, 14, 62);

        doc.setFont('helvetica', 'bold');
        doc.text('CONTRACT DETAILS:', 110, 46);
        doc.setFont('helvetica', 'normal');
        doc.text(`Consultant: ${pdfData.employeeName}`, 110, 52);
        doc.text(`Period: ${pdfData.contractPeriod}`, 110, 57);

        doc.line(14, 68, 196, 68);

        doc.setFont('helvetica', 'bold');
        doc.setTextColor(26, 35, 126);
        doc.text('BANK DETAILS (FOR REMITTANCE):', 14, 75);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(50, 50, 50);
        doc.text(`Bank Name: ${pdfData.bankName}`, 14, 81);
        doc.text(`Account No: ${pdfData.accountNumber}`, 14, 86);
        doc.text(`IFSC Code: ${pdfData.ifscCode}`, 14, 91);

        doc.line(14, 98, 196, 98);

        doc.autoTable({
            startY: 104,
            head: [['Description', 'Billing Rate', 'Quantity/Hours', 'Subtotal']],
            body: [[
                `Professional Services & Deliverables rendered by ${pdfData.employeeName}`,
                `${currencySym} ${parseFloat(pdfData.hourlyRate).toFixed(2)}/hr`,
                `${pdfData.totalHours} hrs`,
                `${currencySym} ${parseFloat(pdfData.contractTotal).toFixed(2)}`
            ]],
            theme: 'grid',
            headStyles: { fillColor: [26, 35, 126] }
        });

        const sub = parseFloat(pdfData.contractTotal) || 0;
        const gst = sub * ((parseFloat(pdfData.gstRate) || 0) / 100);
        const tds = sub * ((parseFloat(pdfData.tdsRate) || 0) / 100);
        const tot = sub + gst - tds;

        let currentY = doc.lastAutoTable.finalY + 10;
        doc.text(`Service Subtotal: ${currencySym} ${sub.toFixed(2)}`, 120, currentY);
        doc.text(`GST (${pdfData.gstRate}%): (+) ${currencySym} ${gst.toFixed(2)}`, 120, currentY + 5);
        doc.text(`TDS (${pdfData.tdsRate}%): (-) ${currencySym} ${tds.toFixed(2)}`, 120, currentY + 10);
        doc.setFont('helvetica', 'bold');
        doc.text(`Net Payable: ${currencySym} ${tot.toFixed(2)}`, 120, currentY + 18);

        const pdfBlob = doc.output('blob');
        const pdfUrl = URL.createObjectURL(pdfBlob);
        window.open(pdfUrl, '_blank');
    };

    const filteredCostCenters = selectedCostCenterId === 'all'
        ? costCenters
        : costCenters.filter(c => c.id === Number(selectedCostCenterId));

    const totalCompanySpent = costCenters.reduce((sum, c) => sum + (c.totalExpenditure || 0), 0) || 1;

    // --- GRAPH 1: Bar Chart - Per cost center stacked (Salary + Direct + Agency) ---
    const costCenterStackedData = filteredCostCenters.map(cc => ({
        name: cc.code,
        fullName: cc.name,
        employeeSalary: cc.employeeSalarySum || 0,
        directContractor: cc.directContractorSum || 0,
        agencyContractor: cc.agencyContractorSum || 0
    }));

    // --- GRAPH 2: Pie Chart - Company-wide split (Salary vs Direct vs Agency) ---
    const companyPieData = summary ? [
        { name: 'Employee Salaries', value: summary.employeeSalariesTotal || 0 },
        { name: 'Direct Contractor', value: summary.directContractorsTotal || 0 },
        { name: 'Agency Contractor', value: summary.agencyContractorsTotal || 0 }
    ].filter(d => d.value > 0) : [];

    // --- DETAIL VIEW: calculations from detail data ---
    const detailSalariesSum = detailData?.employees?.reduce((sum, e) => sum + (e.salary || 0), 0) || 0;
    const detailDirectInvSum = detailData?.invoices?.reduce((sum, inv) => {
        if (inv.contractEmployee?.isDirectContractor) return sum + (Number(inv.netPayable) || 0);
        return sum;
    }, 0) || 0;
    const detailAgencyInvSum = detailData?.invoices?.reduce((sum, inv) => {
        if (!inv.contractEmployee?.isDirectContractor) return sum + (Number(inv.netPayable) || 0);
        return sum;
    }, 0) || 0;
    const detailTotalSpent = detailSalariesSum + detailDirectInvSum + detailAgencyInvSum;

    const detailPieData = [
        { name: 'Employee Salaries', value: detailSalariesSum },
        { name: 'Direct Contractor', value: detailDirectInvSum },
        { name: 'Agency Contractor', value: detailAgencyInvSum }
    ].filter(d => d.value > 0);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', p: 10, minHeight: '80vh' }}>
                <CircularProgress size={50} sx={{ color: '#1a237e', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">Loading Finance Manager Dashboard...</Typography>
            </Box>
        );
    }

    // ===== RENDER DETAIL VIEW MODE (NEW SUB-PAGE STYLE WITH BACK ARROW) =====
    if (viewMode === 'detail') {
        return (
            <Box sx={{ p: 4,  minHeight: '100vh' }}>
                {/* Header Row with Back Button */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                    <Tooltip title="Back to Cost Centers List">
                        <IconButton 
                            onClick={handleCloseDetailView} 
                            sx={{ 
                                bgcolor: 'white', 
                                boxShadow: 2, 
                                '&:hover': { bgcolor: '#f0f4f8' },
                                p: 1.5 
                            }}
                        >
                            <ArrowLeft size={24} color="#1a237e" />
                        </IconButton>
                    </Tooltip>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 800,  letterSpacing: '-0.5px' }}>
                            {detailData?.costCenter?.name || 'Cost Center'} ({detailData?.costCenter?.code || 'N/A'})
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Cost Center Utilization Details & Category Expenditure Report
                        </Typography>
                    </Box>
                </Box>

                {detailLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
                        <CircularProgress size={45} />
                    </Box>
                ) : detailData ? (
                    <Box>
                        {/* Utilization Summary Cards */}
                        <Grid container spacing={3} sx={{ mb: 4 }}>
                            <Grid item xs={12} sm={6} md={3}>
                                <Card sx={{ borderRadius: 3, boxShadow: 3, bgcolor: 'white', borderLeft: '5px solid #1a237e' }}>
                                    <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                                        <Typography variant="caption" color="text.secondary" fontWeight={600}>EMPLOYEE SALARY</Typography>
                                        <Typography variant="h5" sx={{ fontWeight: 800, color: '#1a237e', mt: 0.5 }}>
                                            {formatCurrency(detailSalariesSum)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {detailData.employees?.length || 0} Employees
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Card sx={{ borderRadius: 3, boxShadow: 3, bgcolor: 'white', borderLeft: '5px solid #0288d1' }}>
                                    <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                                        <Typography variant="caption" color="text.secondary" fontWeight={600}>DIRECT CONTRACTOR</Typography>
                                        <Typography variant="h5" sx={{ fontWeight: 800, color: '#0288d1', mt: 0.5 }}>
                                            {formatCurrency(detailDirectInvSum)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            From Pro-Forma Invoices
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Card sx={{ borderRadius: 3, boxShadow: 3, bgcolor: 'white', borderLeft: '5px solid #ed6c02' }}>
                                    <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                                        <Typography variant="caption" color="text.secondary" fontWeight={600}>AGENCY CONTRACTOR</Typography>
                                        <Typography variant="h5" sx={{ fontWeight: 800, color: '#ed6c02', mt: 0.5 }}>
                                            {formatCurrency(detailAgencyInvSum)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            From Pro-Forma Invoices
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Card sx={{ borderRadius: 3, boxShadow: 3, background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)', color: 'white' }}>
                                    <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                                        <Typography variant="caption" sx={{ opacity: 0.9 }} fontWeight={600}>TOTAL UTILISED</Typography>
                                        <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5 }}>
                                            {formatCurrency(detailTotalSpent)}
                                        </Typography>
                                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                            All Categories Combined
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>

                        {/* Inner Modal Pie Graph */}
                        {detailPieData.length > 0 && (
                            <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 3, mb: 4 }}>
                                <Typography variant="h6" fontWeight="bold" color="#1a237e" sx={{ mb: 2 }}>
                                    Utilization Distribution: {detailData.costCenter?.name}
                                </Typography>
                                <ResponsiveContainer width="100%" height={280}>
                                    <PieChart>
                                        <Pie data={detailPieData} cx="50%" cy="50%" innerRadius={70} outerRadius={105} paddingAngle={5} dataKey="value">
                                            {detailPieData.map((entry, index) => (
                                                <Cell key={`cell-detail-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <RechartsTooltip formatter={(val) => formatCurrency(val)} />
                                        <Legend />
                                    </PieChart>
                                </ResponsiveContainer>
                            </Paper>
                        )}

                        {/* Tabs for Detailed Lists */}
                        <Paper sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: 3, mb: 4 }}>
                            <Tabs value={detailTab} onChange={(e, val) => setDetailTab(val)} sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: '#f8f9fa' }}>
                                <Tab label={`Projects (${detailData.projects?.length || 0})`} icon={<Folder size={18} />} iconPosition="start" />
                                <Tab label={`Employee Salary (${detailData.employees?.length || 0})`} icon={<UserCheck size={18} />} iconPosition="start" />
                                <Tab label={`Contractor Invoices (${detailData.invoices?.length || 0})`} icon={<FileText size={18} />} iconPosition="start" />
                                <Tab label={`IT Assets (${detailData.assets?.length || 0})`} icon={<Laptop size={18} />} iconPosition="start" />
                            </Tabs>

                            <Box sx={{ p: 3 }}>
                                {/* Tab 0: Projects */}
                                {detailTab === 0 && (
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Project Name</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Project Key</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Budget</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Timeline</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {detailData.projects?.map((proj) => (
                                                    <TableRow key={proj.project_id || proj.id}>
                                                        <TableCell sx={{ fontWeight: 600, color: '#1a237e' }}>{proj.name}</TableCell>
                                                        <TableCell><Chip label={proj.key || 'N/A'} size="small" variant="outlined" /></TableCell>
                                                        <TableCell>{proj.projectType}</TableCell>
                                                        <TableCell sx={{ fontWeight: 'bold', color: '#2e7d32' }}>{formatCurrency(proj.budget)}</TableCell>
                                                        <TableCell>{proj.start_date && proj.end_date ? `${formatDate(proj.start_date)} - ${formatDate(proj.end_date)}` : 'N/A'}</TableCell>
                                                        <TableCell><Chip label={proj.status} color="success" size="small" /></TableCell>
                                                    </TableRow>
                                                ))}
                                                {detailData.projects?.length === 0 && (
                                                    <TableRow><TableCell colSpan={6} align="center">No projects linked.</TableCell></TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}

                                {/* Tab 1: Employee Salary */}
                                {detailTab === 1 && (
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Employee Name</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Designation</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Department</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Net Salary</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {detailData.employees?.map((emp) => (
                                                    <TableRow key={emp.employee_id}>
                                                        <TableCell sx={{ fontWeight: 500 }}>{emp.firstName} {emp.lastName}</TableCell>
                                                        <TableCell>{emp.employee_id}</TableCell>
                                                        <TableCell>{emp.designation || 'N/A'}</TableCell>
                                                        <TableCell>{emp.department || 'N/A'}</TableCell>
                                                        <TableCell sx={{ fontWeight: 'bold', color: '#1a237e' }}>{formatCurrency(emp.salary)}</TableCell>
                                                    </TableRow>
                                                ))}
                                                {detailData.employees?.length === 0 && (
                                                    <TableRow><TableCell colSpan={5} align="center">No employees assigned.</TableCell></TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}

                                {/* Tab 2: Contractor Invoices */}
                                {detailTab === 2 && (
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Invoice #</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Contractor</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Rate</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Hours</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Subtotal</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>GST</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>TDS</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Net Amount</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Action</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {detailData.invoices?.map((inv) => (
                                                    <TableRow key={inv.invoice_id}>
                                                        <TableCell sx={{ fontWeight: 'bold' }}>{inv.invoiceNumber}</TableCell>
                                                        <TableCell>{inv.contractEmployee?.fullName || inv.contract_employee_id}</TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={inv.contractEmployee?.isDirectContractor ? 'Direct' : 'Agency'}
                                                                size="small"
                                                                color={inv.contractEmployee?.isDirectContractor ? 'primary' : 'warning'}
                                                            />
                                                        </TableCell>
                                                        <TableCell>{formatDate(inv.invoiceDate)}</TableCell>
                                                        <TableCell>{formatCurrency(inv.hourlyRate)}</TableCell>
                                                        <TableCell>{inv.totalHours} hrs</TableCell>
                                                        <TableCell>{formatCurrency(inv.subtotal)}</TableCell>
                                                        <TableCell>{formatCurrency(inv.gstAmount)}</TableCell>
                                                        <TableCell>{formatCurrency(inv.tdsAmount)}</TableCell>
                                                        <TableCell sx={{ fontWeight: 'bold' }}>{formatCurrency(inv.netPayable)}</TableCell>
                                                        <TableCell><Chip label={inv.status} size="small" color={inv.status === 'Paid' ? 'success' : 'warning'} /></TableCell>
                                                        <TableCell>
                                                            <Button 
                                                                size="small" 
                                                                variant="outlined" 
                                                                onClick={() => handleViewInvoiceDocument(inv)}
                                                                sx={{ textTransform: 'none', borderColor: '#1a237e', color: '#1a237e' }}
                                                            >
                                                                View
                                                            </Button>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                                {detailData.invoices?.length === 0 && (
                                                    <TableRow><TableCell colSpan={12} align="center">No invoices generated.</TableCell></TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}

                                {/* Tab 3: IT Assets */}
                                {detailTab === 3 && (
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Asset Name</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Serial / Model</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Issued To</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Issued Date</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {detailData.assets?.map((a) => (
                                                    <TableRow key={a.id}>
                                                        <TableCell sx={{ fontWeight: 500 }}>{a.assetName}</TableCell>
                                                        <TableCell>{a.assetType}</TableCell>
                                                        <TableCell>{a.model} ({a.serialNumber})</TableCell>
                                                        <TableCell>{a.employee ? `${a.employee.firstName} ${a.employee.lastName}` : a.employee_id}</TableCell>
                                                        <TableCell>{a.issuedDate ? formatDate(a.issuedDate) : 'N/A'}</TableCell>
                                                        <TableCell><Chip label={a.status} size="small" color="info" /></TableCell>
                                                    </TableRow>
                                                ))}
                                                {detailData.assets?.length === 0 && (
                                                    <TableRow><TableCell colSpan={6} align="center">No assets found.</TableCell></TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </Box>
                        </Paper>
                    </Box>
                ) : null}
            </Box>
        );
    }

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const item = costCenterStackedData.find(d => d.name === label);
            const title = item ? `${item.fullName} (${label})` : label;
            const total = payload.reduce((sum, entry) => sum + (Number(entry.value) || 0), 0);
            
            return (
                <div style={{ backgroundColor: '#fff', border: '1px solid #ccc', padding: '10px', borderRadius: '4.5px', boxShadow: '0 2px 6px rgba(0,0,0,0.15)' }}>
                    <p style={{ margin: 0, fontWeight: 'bold', color: '#1a237e', fontSize: '0.9rem', marginBottom: '4px' }}>{title}</p>
                    {payload.map((entry, index) => (
                        <p key={`tooltip-${index}`} style={{ margin: '2px 0', color: entry.color || '#333', fontSize: '0.85rem' }}>
                            {entry.name} : {formatCurrency(entry.value)}
                        </p>
                    ))}
                    <hr style={{ margin: '6px 0', borderColor: '#e0e0e0', borderStyle: 'solid' }} />
                    <p style={{ margin: 0, fontWeight: 'bold', color: '#2e7d32', fontSize: '0.9rem' }}>
                        Total Utilised : {formatCurrency(total)}
                    </p>
                </div>
            );
        }
        return null;
    };
const formatYAxis = (value) => {
  if (value >= 10000000) {
    const crore = value / 10000000;

    if (crore >= 1000) {
      return `${(crore / 1000).toFixed(1)}K Cr`; // 1,000 Cr = 1K Cr
    }

    return `${crore.toFixed(1)} Cr`;
  }

  if (value >= 100000) {
    return `${(value / 100000).toFixed(1)} L`;
  }

  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)} K`;
  }

  return value;
};
    // ===== RENDER MAIN LIST VIEW MODE =====
    return (
        <Box sx={{ p: 4,  minHeight: '100vh' }}>
            {/* Top Bar */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.5px' }}>
                        Cost Center Dashboard
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Cost Center Expenditure Analytics — Employee Salaries, Direct Contractors & Agency Contractors
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Button
                        variant="contained"
                        startIcon={<DownloadIcon />}
                        onClick={handleExportExcel}
                        sx={{ 
                            bgcolor: '#1a237e', 
                            '&:hover': { bgcolor: '#283593' }, 
                            textTransform: 'none', 
                            fontWeight: 'bold',
                            borderRadius: 2,
                            px: 2.5
                        }}
                    >
                        Export
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<PdfIcon />}
                        onClick={handleExportPDF}
                        sx={{ 
                            bgcolor: '#c62828', 
                            '&:hover': { bgcolor: '#b71c1c' }, 
                            textTransform: 'none', 
                            fontWeight: 'bold',
                            borderRadius: 2,
                            px: 2.5
                        }}
                    >
                        Export
                    </Button>
                    
                    <TextField
                        select
                        size="small"
                        value={selectedCostCenterId}
                        onChange={(e) => setSelectedCostCenterId(e.target.value)}
                        sx={{ minWidth: 240, bgcolor: 'white', borderRadius: 1 }}
                        label="Filter Cost Center"
                    >
                        <MenuItem value="all">
                            <em>🏢 All Cost Centers ({costCenters.length})</em>
                        </MenuItem>
                        {costCenters.map((cc) => (
                            <MenuItem key={cc.id} value={cc.id}>
                                {cc.name} ({cc.code})
                            </MenuItem>
                        ))}
                    </TextField>
                    <Tooltip title="Refresh Dashboard">
                        <IconButton onClick={fetchDashboardData} sx={{ bgcolor: 'white', boxShadow: 1 }}>
                            <RefreshCw size={20} color="#1a237e" />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
            )}

            {/* ===== 2 CHARTS ===== */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                {/* GRAPH 1: Stacked Bar - Per Cost Center breakdown (Salary + Direct + Agency) */}
                <Grid item xs={12} md={8}>
                    <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <BarChart3 size={22} color="#1a237e" />
                            <Typography variant="h6" fontWeight="bold" color="#1a237e">
                                Expenditure Breakdown Per Cost Center
                            </Typography>
                        </Box>
                        {costCenterStackedData.length > 0 ? (
                            <ResponsiveContainer width="100%" height={320}>
                                <BarChart data={costCenterStackedData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="name" />
                                    <YAxis tickFormatter={formatYAxis} />
                                    <RechartsTooltip content={<CustomTooltip />} />
                                    <Legend />
                                    <Bar dataKey="employeeSalary" name="Employee Salaries" fill="#1a237e" stackId="a" radius={[0, 0, 0, 0]} />
                                    <Bar dataKey="directContractor" name="Direct Contractor" fill="#0288d1" stackId="a" radius={[0, 0, 0, 0]} />
                                    <Bar dataKey="agencyContractor" name="Agency Contractor" fill="#ed6c02" stackId="a" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 320 }}>
                                <Typography color="text.secondary">No cost center data available</Typography>
                            </Box>
                        )}
                    </Paper>
                </Grid>

                {/* GRAPH 2: Pie Chart - Company-wide 3-way split */}
                <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 3, height: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <PieChartIcon size={22} color="#1a237e" />
                            <Typography variant="h6" fontWeight="bold" color="#1a237e">
                                Overall Expenditure Split
                            </Typography>
                        </Box>
                        {companyPieData.length > 0 ? (
                            <ResponsiveContainer width="100%" height={280}>
                                <PieChart>
                                    <Pie data={companyPieData} cx="50%" cy="50%" innerRadius={60} outerRadius={95} paddingAngle={5} dataKey="value">
                                        {companyPieData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <RechartsTooltip formatter={(val) => formatCurrency(val)} />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        ) : (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 280 }}>
                                <Typography color="text.secondary">No expenditure data</Typography>
                            </Box>
                        )}
                    </Paper>
                </Grid>
            </Grid>

            {/* ===== COST CENTER TABLE ===== */}
            <Paper sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: 3, mb: 4 }}>
                <Box sx={{ p: 2.5, bgcolor: '#1a237e', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Building2 size={24} />
                        <Typography variant="h6" fontWeight="bold">
                            Cost Center Utilization Table
                        </Typography>
                    </Box>
                    <Chip label="💡 Click any row for detailed view" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
                </Box>
                <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table sx={{ minWidth: 900 }}>
                        <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Cost Center</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Employee Salary</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Direct Contractor</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Agency Contractor</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Total Utilised</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Employees</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Contractors</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Projects</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredCostCenters.map((cc) => {
                                const percentage = totalCompanySpent > 0 ? ((cc.totalExpenditure / totalCompanySpent) * 100) : 0;
                                let barColor = '#2e7d32'; // Green below 25%
                                if (percentage >= 75) {
                                    barColor = '#d32f2f'; // Red for >= 75%
                                } else if (percentage >= 50) {
                                    barColor = '#ed6c02'; // Orange for >= 50%
                                } else if (percentage >= 25) {
                                    barColor = '#0288d1'; // Blue for >= 25%
                                }

                                return (
                                    <TableRow
                                        key={cc.id}
                                        hover
                                        onClick={() => handleOpenDetailView(cc.id)}
                                        sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f0f4f8' } }}
                                    >
                                        <TableCell>
                                            <Typography variant="subtitle2" fontWeight="bold" color="primary.dark">
                                                {cc.name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {cc.code}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ color: '#1a237e', fontWeight: 600 }}>
                                            {formatCurrency(cc.employeeSalarySum)}
                                        </TableCell>
                                        <TableCell sx={{ color: '#0288d1', fontWeight: 600 }}>
                                            {formatCurrency(cc.directContractorSum)}
                                        </TableCell>
                                        <TableCell sx={{ color: '#ed6c02', fontWeight: 600 }}>
                                            {formatCurrency(cc.agencyContractorSum)}
                                        </TableCell>
                                        <TableCell sx={{ minWidth: 180 }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1a237e', fontSize: '0.95rem' }}>
                                                {formatCurrency(cc.totalExpenditure)}
                                            </Typography>
                                            <Box sx={{ width: '100%', mt: 0.5 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <Typography variant="caption" fontWeight="bold" sx={{ color: barColor }}>
                                                        {percentage.toFixed(1)}% Share
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ width: '100%', height: 6, bgcolor: '#e0e0e0', borderRadius: 3, overflow: 'hidden', mt: 0.2 }}>
                                                    <Box sx={{ 
                                                        width: `${Math.min(percentage, 100)}%`, 
                                                        height: '100%', 
                                                        bgcolor: barColor, 
                                                        borderRadius: 3 
                                                     }} />
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={cc.employeeCount || 0} size="small" color="primary" variant="outlined" />
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={cc.contractorCount || 0} size="small" color="warning" variant="outlined" />
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={cc.projectsCount || 0} size="small" color="success" variant="outlined" />
                                        </TableCell>
                                     

                                        <TableCell>
                                        <Tooltip title="View Details">
                                            <IconButton
                                            size="small"
                                            sx={{
                                                color: '#1a237e',
                                            }}
                                            >
                                            <VisibilityIcon />
                                            </IconButton>
                                        </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {filteredCostCenters.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                                        <Typography color="text.secondary">No cost centers found</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(event, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(event) => {
                        setRowsPerPage(parseInt(event.target.value, 10));
                        setPage(0);
                    }}
                />
            </Paper>
        </Box>
    );
};

export default CostCenterDashboard;
